import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useAuth } from '../contexts/AuthContext';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  BarChart3, TrendingUp, TrendingDown, Building2, Zap,
  ArrowUpRight, ArrowDownRight, ExternalLink, Loader2,
  Users, Calendar, Search, Filter, Download, AlertTriangle,
  Settings2, X, Flame, ChevronRight, FileText
} from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend, Cell, PieChart, Pie
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const formatNum = (v) => {
  if (!v && v !== 0) return '-';
  return Math.round(v).toLocaleString('fr-FR');
};

const EvolutionBadge = ({ value }) => {
  if (value === 0 || value === null || value === undefined) return <span className="text-muted-foreground text-xs">-</span>;
  const isGood = value < 0;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-bold tracking-tight ${
      isGood
        ? 'bg-emeraude/10 text-emeraude'
        : 'bg-amber-500/10 text-amber-600'
    }`} data-testid="evolution-badge">
      {isGood ? <ArrowDownRight className="w-3 h-3" /> : <ArrowUpRight className="w-3 h-3" />}
      {value > 0 ? '+' : ''}{value.toFixed(1)}%
    </span>
  );
};

const CHART_COLORS = ['#397DFF', '#0FB96A', '#F9C80E', '#566C72', '#191D1E', '#93959F'];
const PDF_COLORS = { azur: [57, 125, 255], anthracite: [25, 29, 30], gris: [147, 155, 160], blanc: [245, 246, 249], emeraude: [16, 209, 131], ambre: [249, 200, 14], white: [255, 255, 255] };

export default function MultiClientDashboardPage() {
  const { user, getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterPeriod, setFilterPeriod] = useState('');
  const [alertThreshold, setAlertThreshold] = useState(10);
  const [showThresholdConfig, setShowThresholdConfig] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType) params.set('report_type', filterType);
      if (filterPeriod) params.set('period', filterPeriod);
      if (searchTerm) params.set('search', searchTerm);
      params.set('alert_threshold', alertThreshold);
      const qs = params.toString();
      const res = await axios.get(`${API}/api/dashboard/multi-client?${qs}`, { headers: getAuthHeader() });
      if (res.data.success) setData(res.data.data);
    } catch (err) {
      setError('Impossible de charger le dashboard');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, filterType, filterPeriod, searchTerm, alertThreshold]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const searchTimeout = useRef(null);
  const handleSearchChange = (val) => {
    setSearchTerm(val);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => fetchDashboard(), 400);
  };

  // PDF Export
  const handleExportPDF = async () => {
    if (!data) return;
    setExporting(true);
    try {
      const doc = new jsPDF('l', 'mm', 'a4');
      const W = doc.internal.pageSize.getWidth();
      const H = doc.internal.pageSize.getHeight();
      const m = 15;

      doc.setFillColor(...PDF_COLORS.anthracite);
      doc.rect(0, 0, W, 30, 'F');
      doc.setTextColor(...PDF_COLORS.white);
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('TABLEAU DE BORD MULTI-CLIENT', m, 20);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      const dateStr = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
      doc.text(`Genere le ${dateStr}`, W - m, 20, { align: 'right' });

      const { kpis } = data;
      const boxW = 60, boxH = 35, gap = 8, startY = 42;
      const kpiItems = [
        { label: 'CLIENTS', value: `${kpis.total_clients}`, sub: `${kpis.total_sites} sites` },
        { label: 'CONSO. ANNUELLE', value: `${formatNum(kpis.total_ytd_mwh)} MWh`, sub: `N-1: ${formatNum(kpis.total_prev_year_mwh)} MWh` },
        { label: 'MOIS EN COURS', value: `${formatNum(kpis.total_current_month_mwh)} MWh`, sub: `N-1: ${formatNum(kpis.total_prev_month_mwh)} MWh` },
        { label: 'EVOLUTION ANNUELLE', value: `${kpis.ytd_evolution_pct > 0 ? '+' : ''}${kpis.ytd_evolution_pct}%`, sub: 'vs N-1', color: kpis.ytd_evolution_pct < 0 ? PDF_COLORS.emeraude : PDF_COLORS.ambre },
      ];
      kpiItems.forEach((item, i) => {
        const x = m + i * (boxW + gap);
        doc.setFillColor(...PDF_COLORS.blanc);
        doc.roundedRect(x, startY, boxW, boxH, 3, 3, 'F');
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.3);
        doc.roundedRect(x, startY, boxW, boxH, 3, 3, 'S');
        doc.setTextColor(...PDF_COLORS.gris);
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.text(item.label, x + 5, startY + 10);
        doc.setTextColor(...(item.color || PDF_COLORS.anthracite));
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text(item.value, x + 5, startY + 24);
        doc.setTextColor(...PDF_COLORS.gris);
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.text(item.sub, x + 5, startY + 31);
      });

      const alerts = data.alerts || [];
      let tableStartY = startY + boxH + 15;
      if (alerts.length > 0) {
        doc.setFillColor(255, 243, 224);
        doc.roundedRect(m, tableStartY - 5, W - 2 * m, 8 + alerts.length * 5, 2, 2, 'F');
        doc.setTextColor(180, 100, 0);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text(`ALERTES SURCONSOMMATION (seuil: +${data.alert_threshold}%)`, m + 4, tableStartY + 1);
        alerts.forEach((a, i) => {
          doc.setFontSize(8);
          doc.setFont('helvetica', 'normal');
          doc.text(`${a.client}: +${a.evolution_pct}% (${formatNum(a.current_month)} vs ${formatNum(a.previous_month)} MWh)`, m + 6, tableStartY + 7 + i * 5);
        });
        tableStartY += 12 + alerts.length * 5;
      }

      doc.setTextColor(...PDF_COLORS.anthracite);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('CLASSEMENT DES CLIENTS', m, tableStartY);

      const tableData = (data.ranking || []).map((r, i) => [
        (i + 1).toString(),
        r.name,
        r.report_type === 'consumption_multisites' ? 'Multi' : 'Mono',
        r.sites_count.toString(),
        `${formatNum(r.current_month)} MWh`,
        r.evolution_pct !== 0 ? `${r.evolution_pct > 0 ? '+' : ''}${r.evolution_pct.toFixed(1)}%` : '-',
        `${formatNum(r.ytd)} MWh`,
        r.ytd_evolution_pct !== 0 ? `${r.ytd_evolution_pct > 0 ? '+' : ''}${r.ytd_evolution_pct.toFixed(1)}%` : '-',
      ]);

      autoTable(doc, {
        startY: tableStartY + 4,
        head: [['#', 'Client', 'Type', 'Sites', 'Mois en cours', 'Evol.', 'Conso. annuelle', 'Evol. annuelle']],
        body: tableData,
        theme: 'plain',
        styles: { fontSize: 7, cellPadding: 2 },
        headStyles: { fillColor: PDF_COLORS.azur, textColor: PDF_COLORS.white, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: PDF_COLORS.blanc },
        columnStyles: {
          0: { cellWidth: 8, halign: 'center' },
          1: { cellWidth: 'auto' },
          2: { cellWidth: 14, halign: 'center' },
          3: { cellWidth: 12, halign: 'center' },
          4: { cellWidth: 28, halign: 'right' },
          5: { cellWidth: 18, halign: 'center' },
          6: { cellWidth: 28, halign: 'right', fontStyle: 'bold' },
          7: { cellWidth: 18, halign: 'center' },
        },
        margin: { left: m, right: m },
        didParseCell: (d) => {
          if ((d.column.index === 5 || d.column.index === 7) && d.section === 'body') {
            const val = parseFloat(d.cell.raw);
            if (!isNaN(val)) {
              d.cell.styles.textColor = val < 0 ? PDF_COLORS.emeraude : val > 0 ? PDF_COLORS.ambre : PDF_COLORS.gris;
              d.cell.styles.fontStyle = 'bold';
            }
          }
        },
      });

      doc.setDrawColor(...PDF_COLORS.azur);
      doc.setLineWidth(0.5);
      doc.line(m, H - 15, W - m, H - 15);
      doc.setTextColor(...PDF_COLORS.gris);
      doc.setFontSize(7);
      doc.text('Place des Energies - www.placedesenergies.com', m, H - 10);

      doc.save(`dashboard_multi_client_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (err) {
      console.error('PDF export error:', err);
    } finally {
      setExporting(false);
    }
  };

  if (loading && !data) return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    </div>
  );

  if (error && !data) return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <div className="container mx-auto px-4 py-16 text-center text-muted-foreground">{error}</div>
    </div>
  );

  const { kpis, ranking, monthly_comparison, alerts, available_periods, alert_threshold: serverThreshold } = data || {};

  const top10 = (ranking || []).slice(0, 10).map(r => ({
    name: r.name.length > 18 ? r.name.substring(0, 16) + '...' : r.name,
    fullName: r.name,
    ytd: r.ytd,
  }));

  // Donut chart data: Monosite vs Multisite
  const typeBreakdown = (ranking || []).reduce((acc, r) => {
    const isMono = r.report_type === 'consumption';
    const key = isMono ? 'mono' : 'multi';
    acc[key].mwh += r.ytd || 0;
    acc[key].count += 1;
    return acc;
  }, { mono: { mwh: 0, count: 0 }, multi: { mwh: 0, count: 0 } });

  const donutData = [
    { name: 'Monosite', value: Math.round(typeBreakdown.mono.mwh * 10) / 10, count: typeBreakdown.mono.count, fill: '#0FB96A' },
    { name: 'Multisite', value: Math.round(typeBreakdown.multi.mwh * 10) / 10, count: typeBreakdown.multi.count, fill: '#397DFF' },
  ].filter(d => d.value > 0);
  const donutTotal = donutData.reduce((s, d) => s + d.value, 0);

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white border border-border rounded-lg shadow-xl p-3 text-sm">
        <p className="font-bold text-foreground mb-1">{payload[0]?.payload?.fullName}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color }}>{p.name}: <span className="font-bold">{formatNum(p.value)} MWh</span></p>
        ))}
      </div>
    );
  };

  const MonthlyTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white border border-border rounded-lg shadow-xl p-3 text-sm">
        <p className="font-bold text-foreground mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color }}>{p.name}: <span className="font-bold">{formatNum(p.value)} MWh</span></p>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background" data-testid="multi-client-dashboard">
      <Navigation />
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-4xl sm:text-5xl font-bold font-display text-foreground" data-testid="dashboard-title">
              Tableau de bord
            </h1>
            <p className="text-sm text-muted-foreground mt-2">
              Vue consolid&eacute;e de tous les rapports &eacute;nerg&eacute;tiques
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/consumption/reports">
              <Button
                variant="outline"
                className="gap-2 shadow-sm"
                data-testid="view-reports-btn"
              >
                <FileText className="w-4 h-4" />
                Rapports
              </Button>
            </Link>
            <Button
              onClick={handleExportPDF}
              disabled={exporting}
              className="gap-2 shadow-md"
              data-testid="export-pdf-btn"
            >
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Exporter PDF
            </Button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="kpi-cards">
          <Card className="border border-border/60 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="pt-5 pb-4 px-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="p-2 rounded-lg bg-primary/10"><Building2 className="w-4 h-4 text-primary" /></div>
                <span className="text-[11px] text-muted-foreground font-semibold uppercase tracking-widest">Clients</span>
              </div>
              <p className="text-3xl font-bold font-display text-foreground" data-testid="kpi-clients">{kpis?.total_clients || 0}</p>
              <p className="text-xs text-muted-foreground mt-1">{kpis?.total_sites || 0} sites au total</p>
            </CardContent>
          </Card>
          <Card className="border border-border/60 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="pt-5 pb-4 px-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="p-2 rounded-lg bg-emeraude/10"><Zap className="w-4 h-4 text-emeraude" /></div>
                <span className="text-[11px] text-muted-foreground font-semibold uppercase tracking-widest">Conso. annuelle</span>
              </div>
              <p className="text-3xl font-bold font-display text-foreground" data-testid="kpi-ytd">{formatNum(kpis?.total_ytd_mwh)}</p>
              <p className="text-xs text-muted-foreground mt-1">MWh</p>
            </CardContent>
          </Card>
          <Card className="border border-border/60 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="pt-5 pb-4 px-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="p-2 rounded-lg bg-ambre/10"><Calendar className="w-4 h-4 text-ambre" /></div>
                <span className="text-[11px] text-muted-foreground font-semibold uppercase tracking-widest">Mois en cours</span>
              </div>
              <p className="text-3xl font-bold font-display text-foreground" data-testid="kpi-month">{formatNum(kpis?.total_current_month_mwh)}</p>
              <p className="text-xs text-muted-foreground mt-1">MWh</p>
            </CardContent>
          </Card>
          <Card className="border border-border/60 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="pt-5 pb-4 px-5">
              <div className="flex items-center gap-2 mb-3">
                <div className={`p-2 rounded-lg ${(kpis?.ytd_evolution_pct || 0) < 0 ? 'bg-emeraude/10' : 'bg-amber-500/10'}`}>
                  {(kpis?.ytd_evolution_pct || 0) < 0 ? <TrendingDown className="w-4 h-4 text-emeraude" /> : <TrendingUp className="w-4 h-4 text-amber-500" />}
                </div>
                <span className="text-[11px] text-muted-foreground font-semibold uppercase tracking-widest">&Eacute;vol. annuelle</span>
              </div>
              <p className={`text-3xl font-bold font-display ${(kpis?.ytd_evolution_pct || 0) < 0 ? 'text-emeraude' : 'text-amber-500'}`} data-testid="kpi-evolution">
                {(kpis?.ytd_evolution_pct || 0) > 0 ? '+' : ''}{kpis?.ytd_evolution_pct || 0}%
              </p>
              <p className="text-xs text-muted-foreground mt-1">N-1: {formatNum(kpis?.total_prev_year_mwh)} MWh</p>
            </CardContent>
          </Card>
        </div>

        {/* Alerts Section */}
        {alerts && alerts.length > 0 && (
          <Card className="mb-8 border-l-4 border-l-amber-500 border-border/60 shadow-sm overflow-hidden" data-testid="alerts-section">
            <CardHeader className="pb-3 pt-4 px-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-amber-500/10">
                    <Flame className="w-4 h-4 text-amber-500" />
                  </div>
                  <div>
                    <CardTitle className="text-base font-bold font-display text-foreground">
                      Alertes surconsommation
                      <span className="ml-2 text-xs font-semibold text-amber-600 bg-amber-500/10 px-2 py-0.5 rounded-full">{alerts.length}</span>
                    </CardTitle>
                  </div>
                </div>
                <span className="text-[11px] text-muted-foreground font-medium bg-muted/50 px-3 py-1 rounded-full">
                  Seuil: +{serverThreshold}% vs N-1
                </span>
              </div>
            </CardHeader>
            <CardContent className="px-5 pb-4 pt-0">
              <div className="space-y-2">
                {alerts.map((a, i) => (
                  <div key={i} className="flex items-center justify-between bg-muted/30 hover:bg-muted/50 rounded-lg px-4 py-3 transition-colors group">
                    <div className="flex items-center gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                      <span className="font-semibold text-foreground text-sm">{a.client}</span>
                    </div>
                    <div className="flex items-center gap-5 text-sm">
                      <span className="font-bold text-amber-600 bg-amber-500/10 px-2.5 py-0.5 rounded-md">
                        +{a.evolution_pct.toFixed(1)}%
                      </span>
                      <span className="text-muted-foreground text-xs">
                        {formatNum(a.current_month)} vs {formatNum(a.previous_month)} MWh
                      </span>
                      <Link to={a.report_type === 'consumption_multisites' ? `/consumption/multisites/${a.share_token}` : `/consumption/report/${a.share_token}`}>
                        <ChevronRight className="w-4 h-4 text-primary opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer" />
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters Bar */}
        <Card className="mb-8 border-border/60 shadow-sm" data-testid="filters-bar">
          <CardContent className="py-3 px-4">
            <div className="flex flex-wrap items-center gap-3">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <div className="relative flex-1 min-w-[200px] max-w-xs">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Rechercher un client..."
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="pl-9 h-9 text-sm"
                  data-testid="search-input"
                />
              </div>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="h-9 px-3 rounded-md border border-input text-sm text-foreground bg-background focus:ring-2 focus:ring-primary/20 focus:border-primary"
                data-testid="filter-type"
              >
                <option value="">Tous les types</option>
                <option value="consumption">Monosite</option>
                <option value="consumption_multisites">Multisite</option>
              </select>
              <select
                value={filterPeriod}
                onChange={(e) => setFilterPeriod(e.target.value)}
                className="h-9 px-3 rounded-md border border-input text-sm text-foreground bg-background focus:ring-2 focus:ring-primary/20 focus:border-primary"
                data-testid="filter-period"
              >
                <option value="">Toutes les p&eacute;riodes</option>
                {(available_periods || []).map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              <div className="flex items-center gap-1 ml-auto">
                <button
                  onClick={() => setShowThresholdConfig(!showThresholdConfig)}
                  className={`p-2 rounded-md transition-colors ${showThresholdConfig ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                  data-testid="threshold-config-btn"
                  title="Configurer le seuil d'alerte"
                >
                  <Settings2 className="w-4 h-4" />
                </button>
                {(searchTerm || filterType || filterPeriod) && (
                  <button
                    onClick={() => { setSearchTerm(''); setFilterType(''); setFilterPeriod(''); }}
                    className="p-2 rounded-md text-muted-foreground hover:text-destructive transition-colors"
                    data-testid="clear-filters-btn"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Threshold Config */}
        {showThresholdConfig && (
          <Card className="mb-8 border-primary/30 shadow-sm" data-testid="threshold-config">
            <CardContent className="py-4 px-5">
              <div className="flex items-center gap-4">
                <div className="p-2 rounded-lg bg-amber-500/10">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-foreground">Seuil d'alerte surconsommation</p>
                  <p className="text-xs text-muted-foreground">Les clients dont l'&eacute;volution mensuelle d&eacute;passe ce seuil seront signal&eacute;s</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">+</span>
                  <Input
                    type="number"
                    value={alertThreshold}
                    onChange={(e) => setAlertThreshold(Math.max(0, parseFloat(e.target.value) || 0))}
                    className="w-20 h-9 text-center text-sm"
                    data-testid="threshold-input"
                    min="0"
                    step="5"
                  />
                  <span className="text-sm text-muted-foreground">% vs N-1</span>
                  <Button
                    size="sm"
                    onClick={() => { fetchDashboard(); setShowThresholdConfig(false); }}
                    className="h-9"
                    data-testid="apply-threshold-btn"
                  >
                    Appliquer
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <Card className="border-border/60 shadow-sm" data-testid="type-breakdown-chart">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-bold font-display text-foreground flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-primary" />
                R&eacute;partition par type (annuelle)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-6">
                <ResponsiveContainer width="55%" height={260}>
                  <PieChart>
                    <Pie
                      data={donutData}
                      cx="50%"
                      cy="50%"
                      innerRadius={70}
                      outerRadius={110}
                      paddingAngle={3}
                      dataKey="value"
                      stroke="none"
                    >
                      {donutData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                    </Pie>
                    <Tooltip
                      formatter={(value) => [`${value.toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} MWh`, '']}
                      contentStyle={{ borderRadius: 8, border: '1px solid hsl(var(--border))', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-5">
                  {donutData.map((d, i) => (
                    <div key={i}>
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: d.fill }} />
                        <span className="text-sm font-semibold text-foreground">{d.name}</span>
                      </div>
                      <p className="text-2xl font-bold font-display text-foreground ml-5">
                        {d.value.toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} <span className="text-sm font-normal text-muted-foreground">MWh</span>
                      </p>
                      <p className="text-xs text-muted-foreground ml-5">
                        {d.count} client{d.count > 1 ? 's' : ''} &middot; {donutTotal > 0 ? ((d.value / donutTotal) * 100).toFixed(1) : 0}%
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border/60 shadow-sm" data-testid="monthly-chart">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-bold font-display text-foreground flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-emeraude" />
                &Eacute;volution mensuelle (tous clients)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={monthly_comparison} margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
                  <YAxis tickFormatter={v => `${formatNum(v)}`} tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} />
                  <Tooltip content={<MonthlyTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="current_year" name="Annee courante" stroke="#397DFF" strokeWidth={2.5} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                  <Line type="monotone" dataKey="previous_year" name="N-1" stroke="#93959F" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Full Ranking Table */}
        <Card className="border-border/60 shadow-sm" data-testid="ranking-table">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-bold font-display text-foreground flex items-center gap-2">
              <Users className="w-4 h-4 text-primary" />
              Classement des clients ({ranking?.length || 0})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="clients-table">
                <thead>
                  <tr className="bg-muted/50 border-b border-border">
                    <th className="px-4 py-3 text-left text-[11px] font-bold text-muted-foreground uppercase tracking-wider">#</th>
                    <th className="px-4 py-3 text-left text-[11px] font-bold text-muted-foreground uppercase tracking-wider">Client</th>
                    <th className="px-4 py-3 text-left text-[11px] font-bold text-muted-foreground uppercase tracking-wider">Type</th>
                    <th className="px-4 py-3 text-right text-[11px] font-bold text-muted-foreground uppercase tracking-wider">Sites</th>
                    <th className="px-4 py-3 text-right text-[11px] font-bold text-muted-foreground uppercase tracking-wider">Mois en cours</th>
                    <th className="px-4 py-3 text-right text-[11px] font-bold text-muted-foreground uppercase tracking-wider">&Eacute;vol. mois</th>
                    <th className="px-4 py-3 text-right text-[11px] font-bold text-muted-foreground uppercase tracking-wider">Conso. annuelle</th>
                    <th className="px-4 py-3 text-right text-[11px] font-bold text-muted-foreground uppercase tracking-wider">&Eacute;vol. annuelle</th>
                    <th className="px-4 py-3 text-center text-[11px] font-bold text-muted-foreground uppercase tracking-wider">Voir</th>
                  </tr>
                </thead>
                <tbody>
                  {(ranking || []).map((r, i) => (
                    <tr key={i} className={`border-b border-border/50 hover:bg-primary/5 transition-colors ${r.has_alert ? 'bg-amber-500/5' : ''}`}>
                      <td className="px-4 py-3 text-muted-foreground font-medium">
                        <div className="flex items-center gap-1.5">
                          {r.has_alert && <div className="w-1.5 h-1.5 rounded-full bg-amber-500" data-testid={`alert-icon-${i}`} />}
                          {i + 1}
                        </div>
                      </td>
                      <td className="px-4 py-3 font-semibold text-foreground">{r.name}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2.5 py-0.5 rounded-md text-xs font-bold ${
                          r.report_type === 'consumption_multisites' ? 'bg-primary/10 text-primary' : 'bg-emeraude/10 text-emeraude'
                        }`}>
                          {r.report_type === 'consumption_multisites' ? 'Multi' : 'Mono'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-foreground">{r.sites_count || '-'}</td>
                      <td className="px-4 py-3 text-right font-semibold text-primary">{formatNum(r.current_month)} MWh</td>
                      <td className="px-4 py-3 text-right"><EvolutionBadge value={r.evolution_pct} /></td>
                      <td className="px-4 py-3 text-right font-bold text-foreground">{formatNum(r.ytd)} MWh</td>
                      <td className="px-4 py-3 text-right"><EvolutionBadge value={r.ytd_evolution_pct} /></td>
                      <td className="px-4 py-3 text-center">
                        <Link to={r.report_type === 'consumption_multisites' ? `/consumption/multisites/${r.share_token}` : `/consumption/report/${r.share_token}`}>
                          <Button variant="ghost" size="icon" className="h-7 w-7 hover:bg-primary/10" data-testid={`view-report-${i}`}>
                            <ExternalLink className="w-3.5 h-3.5 text-primary" />
                          </Button>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
